
import React from "react";
import * as Hooks from "../../../../hooks";
import { i18nManager } from "../../../../i18n";
import { Schemes, Utils } from "../../../../utils";
import { Elements } from "../../../shared/Elements";
import MediaInput from "../../../shared/MediaInput";
import * as ReactRouterDom from "react-router-dom";
import LoadingGif from "../../../assets/images/loading_circle.gif";
import { Button, ButtonManageRef, DateInput, DOMHelper, Dropdown, FormControl, ObjectHelper, RadioButton, Scheme, Subscriber, TextInput, useSubscription } from "@rivtn/noseur";
import { ApiResponseData, BaseService, CountryService, ICountry, IMember, ITournamentEdition, IHost, SanitizedResponse, HostRole, HostService, TournamentService, HostTestService, TournamentTestService, TournamentStatus } from "../../../../services";
import Details from "./sections/Details";
import ProgramAndParticipant from "./sections/ProgramAndParticipant";
import Media from "./sections/Media";
import Dates from "./sections/Dates";
import Registration from "./sections/Registration";
import CustomFields from "./sections/CustomFields";
import PrizePool from "./sections/PrizePool";
import StageAndFormat from "./sections/StageAndFormat";
import ScoringAndTieBreaking from "./sections/ScoringAndTieBreaking";
import Final from "./sections/Final";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";

export enum CreateTournamentSection {

    DATES = "DATES",
    MEDIA = "MEDIA",
    FINAL = "FINAL",
    DETAILS = "DETAILS",
    PRIZE_POOL = "PRIZE_POOL",
    REGISTRATION = "REGISTRATION",
    CUSTOM_FIELDS = "CUSTOM_FIELDS",
    STAGE_AND_FORMAT = "STAGE_AND_FORMAT",
    PROGRAM_AND_PARTICIPANT = "PROGRAM_AND_PARTICIPANT",
    SCORING_AND_TIE_BREAKING = "SCORING_AND_TIE_BREAKING",

}

export interface CreateTournamentSectionProps {
    active?: boolean;
    profile: IMember;
    hostPublicId: string;
    ref: React.ForwardedRef<HTMLDivElement>;
    publish: (done?: () => void, noSave?: boolean) => void;
    copyFromExistingTournamentEdition: (edition: ITournamentEdition) => void;
    setActiveSection: (section: CreateTournamentSection, done?: () => void, noSave?: boolean) => void;
    tournamentEditionState: [Partial<ITournamentEdition>, React.Dispatch<React.SetStateAction<Partial<ITournamentEdition>>>];
}

export interface CreateTournamentProps {
    profile: IMember;
}

(Dates as any).NAME = CreateTournamentSection.DATES;
(Media as any).NAME = CreateTournamentSection.MEDIA;
(Final as any).NAME = CreateTournamentSection.FINAL;
(Details as any).NAME = CreateTournamentSection.DETAILS;
(PrizePool as any).NAME = CreateTournamentSection.PRIZE_POOL;
(Registration as any).NAME = CreateTournamentSection.REGISTRATION;
(CustomFields as any).NAME = CreateTournamentSection.CUSTOM_FIELDS;
(StageAndFormat as any).NAME = CreateTournamentSection.STAGE_AND_FORMAT;
(ProgramAndParticipant as any).NAME = CreateTournamentSection.PROGRAM_AND_PARTICIPANT;
(ScoringAndTieBreaking as any).NAME = CreateTournamentSection.SCORING_AND_TIE_BREAKING;

function CreateTournament(props: CreateTournamentProps) {

    const { profile } = props;
    const [themeMode, _] = Hooks.useThemeMode();
    const navigate = ReactRouterDom.useNavigate();
    const countryService = CountryService.getInstance();
    const tournamentHostService = HostService.getInstance();
    const labels = i18nManager.Labels.host.create_tournament;
    const tournamentService = TournamentService.getInstance();
    const [countries, setCountries] = React.useState<ICountry[]>();
    const collapseSidebar = useSubscription("OMNEHOST.PAGE.COLLAPSE_SIDEBAR");
    let { id: hostPublicId = "", tournamentEditionId } = ReactRouterDom.useParams();
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const tournamentEditionState = React.useState<Partial<ITournamentEdition>>({
        host_public_id: hostPublicId,
        country_code: profile.country_code,
        registration: {
            [`${i18nManager.getCountryCurrency(profile.country_code)}`]: {
                fee_amount: 0,
                max_participants: 10,
            }
        }
    });
    const theSections = [
        Details,
        ProgramAndParticipant,
        Media,
        Dates,
        Registration,
        CustomFields,
        PrizePool,
        StageAndFormat,
        ScoringAndTieBreaking,
        Final,
    ];
    const [tournamentEdition, setTournamentEdition] = tournamentEditionState;
    const [activeSection, setActiveSection] = React.useState<CreateTournamentSection>((searchParams.get("section") as any) ?? tournamentEdition.extra_data?.active_express_section ?? CreateTournamentSection.DETAILS);
    const [sections, setSections] = React.useState<((props: CreateTournamentSectionProps) => React.JSX.Element)[] | undefined>(theSections);

    React.useEffect(() => {
        fetchInformations();
        setTimeout(() => collapseSidebar(true), 100);
        countryService.queryCountries({ name: "" }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
            setCountries(apiResponse.data!.content);
        }).catch(BaseService.reportError);

        return () => {
            collapseSidebar(Utils.getSetting("csb", false));
        };
    }, []);

    React.useEffect(() => {
        if (!sections) setSections(theSections);
    }, [tournamentEditionState[0]]);

    return (<div className="teams create-tournament">
        <div className="header">
            <div className="t">
                <i className="fa fa-arrow-left" style={{ marginRight: 10, cursor: "pointer" }} onClick={() => window.history.back()} />
                <span style={{ color: "var(--textColor)" }}>{tournamentEditionId ? labels.update_tournament + (tournamentEdition.name ?? "") : labels.create_new_tournament}</span>
            </div>
            <div className="controls">
                <Button raised scheme={Schemes.OMNEHOST} text={tournamentEdition.status === TournamentStatus.DRAFT ? labels.save_draft : labels.save_changes} loadingProps={Elements.BUTTON_LOADING_PROPS}
                    disabled={!tournamentEdition.external_id} onClick={(e) => {
                        e.manageRef?.setLoadingState(true);
                        validateBeforeSectionSwitch(activeSection, () => {
                            e.manageRef?.setLoadingState(false);
                        });
                    }} />
                <Button raised scheme={Schemes.OMNEHOST} text={tournamentEdition.status === TournamentStatus.DRAFT ? labels.publish : labels.go_to_manager_mode} loadingProps={Elements.BUTTON_LOADING_PROPS}
                    disabled={!tournamentEdition.external_id} onClick={(e) => {
                        e.manageRef?.setLoadingState(true);
                        if (tournamentEdition.status === TournamentStatus.DRAFT) {
                            publishTournament(() => {
                                e.manageRef?.setLoadingState(false);
                            });
                            return;
                        }
                        window.location.href = `/hosts/${hostPublicId}/tournament/${tournamentEdition.tournament?.external_id}`;
                    }} />
            </div>
        </div>
        <div className="flows">
            {(!sections) && (<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>{Elements.LOADING_STATE}</div>)}
            {sections?.map((section) => {
                const sectionName = (section as any).NAME;
                const isActive = activeSection === sectionName;

                if (tournamentEditionId && !tournamentEdition.external_id) {
                    return (<React.Fragment key={sectionName}>
                        {Elements.LOADING_STATE}
                    </React.Fragment>)
                }

                return React.createElement(section, {
                    profile,
                    key: sectionName,
                    tournamentEditionState,
                    hostPublicId: hostPublicId,
                    publish: publishTournament,
                    active: activeSection === sectionName,
                    setActiveSection: validateBeforeSectionSwitch,
                    copyFromExistingTournamentEdition: copyFromExistingTournamentEdition,
                    ref: (e) => {
                        if (e && isActive) e.scrollIntoView();
                    },
                });
            })}
        </div>
    </div>);

    function fetchInformations() {
        if (!tournamentEditionId) return;
        tournamentService.getSingleTournamentEdition(tournamentEditionId).then(({ sanitized }) => {
            setTournamentEdition(sanitized);
            if (!searchParams.has("section") && sanitized.extra_data.active_express_section) {
                setActiveSection(sanitized.extra_data.active_express_section);
            }
        }).catch(BaseService.reportError);
    }

    async function copyFromExistingTournamentEdition(edition: ITournamentEdition) {
        setSections(undefined);
        const cloned = { ...ObjectHelper.conditionalClone(edition, (key) => !["external_id", "tournament", "host", "host_id", "dates", "un_reads", "extra_data", "meta"].includes(key)), ...tournamentEditionState[0], is_template: false, program: edition.program, program_id: edition.program?.external_id, };
        tournamentEditionState[1](cloned);
        console.log("SELECTED THE TOURNAMENT", cloned);
    }

    async function validateBeforeSectionSwitch(section: CreateTournamentSection, done?: () => void, noSave: boolean = false) {
        try {
            let sanitized: Partial<ITournamentEdition> = tournamentEdition;
            if (!tournamentEdition.name) {
                throw new Error(labels.errors.name_required);
            } else if (!tournamentEdition.location) {
                throw new Error(labels.errors.location_required);
            } else if (!tournamentEdition.country_code) {
                throw new Error(labels.errors.country_required);
            } else if (section !== CreateTournamentSection.PROGRAM_AND_PARTICIPANT && !(tournamentEdition.program_id || tournamentEdition.program?.external_id)) {
                throw new Error("You need to select a program for your tournament");
            }
            if (!tournamentEdition.external_id && section === CreateTournamentSection.PROGRAM_AND_PARTICIPANT) {
                sanitized = (await tournamentService.createTournamentEdition(tournamentEdition)).sanitized;
                if (!tournamentEditionId) {
                    navigate(window.location.pathname + '/' + sanitized.external_id, { replace: true });
                }
            } else if (!noSave) {
                sanitized = (await tournamentService.updateTournamentEdition({ ...tournamentEdition, extra_data: { ...tournamentEdition.extra_data, active_express_section: section } })).sanitized;
                Utils.updateSearchParams("section", section, setSearchParams);
            }
            setTournamentEdition(sanitized);
            setActiveSection(section);
        } catch (err: any) {
            Siren.pop(`${err}`);
        }
        done?.();
    }

    async function publishTournament(done?: () => void) {
        if (!tournamentEdition.external_id) return;

        tournamentService.updateTournamentEditionStatus(tournamentEdition.external_id!, TournamentStatus.UPCOMING).then(() => {
            const dialogLabels = labels.dialog.published;
            const tournamentLink = Utils.buildPublicUrl("TOURNAMENT", tournamentEdition.tournament?.public_id!);
            const dialog = ConfirmDialog({
                closable: true,
                noControl: true,
                dismissibleModal: true,
                title: dialogLabels.title,
                content: (<div style={{ marginTop: 10 }}>
                    <span>{dialogLabels.desc}</span> <a target="_blank" href={tournamentLink}>{tournamentLink}</a>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", margin: 30, flexDirection: "column" }}>
                        <img alt={tournamentEdition.name} src={tournamentEdition.logo_url} style={{ width: 90, height: 90, borderRadius: "100%", border: "5px solid var(--mainColor)", marginBottom: 10 }} />
                        <span className="lined-limit" style={{ fontSize: 18, fontWeight: "bold" }}>{tournamentEdition.name}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                        <Button raised scheme={Schemes.OMNEHOST} style={{ width: "100%", margin: "5px 0px" }} text={dialogLabels.go_to_manager_mode} onClick={() => {
                            dialog.hide();
                        }} />
                        <Button raised scheme={Schemes.OMNEHOST} style={{ width: "100%", margin: "5px 0px" }} text={dialogLabels.visit_page} link={tournamentLink} linkTarget="_blank" onClick={() => {
                            dialog.hide();
                        }} />
                        <Button raised scheme={Schemes.OMNEHOST} style={{ width: "100%", margin: "5px 0px" }} text={dialogLabels.copy_link} onClick={() => {
                            DOMHelper.copyToClipboard(tournamentLink, () => {
                                Siren.alert(i18nManager.Labels.common.copied, { lifetime: 1000 });
                                dialog.hide();
                            });
                        }} />
                    </div>
                </div>)
            });
        }).catch(BaseService.reportError).finally(done);
    }

}

export default CreateTournament;
